<template>
  <div>
    <div class="container">
      <app-bar
        title="Survei Kepuasan Pelanggan"
        :previousLink="{ name: 'Hasil' }"
      />
    </div>

    <!-- Hero -->
    <div class="bg-primary text-white text-center py-4 px-3">
      <h5 class="fw-700 fs-18 lh-28 mb-0">Survei Kepuasan Pelanggan</h5>
    </div>

    <div class="container py-4">
      <!-- Star Rating -->
      <section class="text-center mb-4">
        <p class="fw-600 fs-14 color-neutral-900 mb-3">
          Bagaimana pelayanan kami hari ini?
        </p>
        <div class="d-flex align-items-center justify-content-center gap-3">
          <i
            v-for="star in 5"
            :key="star"
            class="star-icon ri-star-fill"
            :class="{
              'star-active': star <= (hoveredStar || rating),
              'star-inactive': star > (hoveredStar || rating)
            }"
            @mouseenter="hoveredStar = star"
            @mouseleave="hoveredStar = 0"
            @click="onSelectRating(star)"
          ></i>
        </div>
      </section>

      <template v-if="rating > 0">
        <!-- Service Improvement Cards -->
        <section class="mb-4">
          <p
            v-if="komentarTitle"
            class="fw-600 fs-14 color-neutral-900 text-center mb-3"
          >
            {{ komentarTitle }} <br />(pilih minimal 3)
          </p>
          <Loading v-if="loading.komentarList" />
          <div v-else class="row g-2 align-items-stretch">
            <div
              v-for="service in komentarList"
              :key="service.idsurvei"
              class="col-4 d-flex"
            >
              <div
                class="service-card text-center py-3 px-2 rounded-3 w-100 d-flex align-items-center justify-content-center"
                :class="{
                  'service-card-active': isServiceSelected(service),
                  'service-card-inactive': !isServiceSelected(service)
                }"
                @click="toggleService(service)"
              >
                <span class="fw-500 fs-11">{{ service.keterangan }}</span>
              </div>
            </div>
          </div>
        </section>

        <!-- Comment -->
        <section class="mb-4">
          <p class="fw-600 fs-14 color-neutral-900 mb-2">Komentar (opsional)</p>
          <textarea
            class="form-control fs-12"
            rows="3"
            placeholder="Tulis komentar Anda..."
            v-model="komentar"
          ></textarea>
        </section>

        <!-- Recommendation -->
        <section class="mb-4">
          <div class="recommendation-card bg-neutral-100 rounded-3 p-3">
            <p class="fw-600 fs-14 color-neutral-900 mb-3">
              Apakah Anda akan merekomendasikan layanan ini ke keluarga atau
              teman?
            </p>
            <div class="d-flex justify-content-center gap-4">
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  name="recommendation"
                  id="recommendYes"
                  value="Ya"
                  v-model="rekomendasi"
                />
                <label class="form-check-label fw-500 fs-12" for="recommendYes">
                  Ya
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  name="recommendation"
                  id="recommendNo"
                  value="Tidak"
                  v-model="rekomendasi"
                />
                <label class="form-check-label fw-500 fs-12" for="recommendNo">
                  Tidak
                </label>
              </div>
            </div>
          </div>
        </section>

        <!-- Submit -->
        <button
          class="btn btn-primary w-100 py-3 fw-700 fs-14"
          :disabled="!isFormComplete || loading.submitRating"
          @click="submitSurvei"
        >
          {{ loading.submitRating ? "Mengirim..." : "Kirim" }}
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppBar from '@/components/AppBar.vue'
import Loading from '@/components/utility/Loading'
import { title } from '@/core/page'
import { showNotif } from '@/core/utility'
import {
  getKomentarList,
  komentarList,
  komentarTitle,
  submitSurveiRating,
  loading
} from '@/core/hasil'

export default {
  components: { AppBar, Loading },
  setup () {
    const route = useRoute()
    const router = useRouter()

    const rating = ref(0)
    const hoveredStar = ref(0)
    const komentar = ref('')
    const rekomendasi = ref(null)
    const selectedServices = ref([])

    const isServiceSelected = service => {
      return selectedServices.value.some(s => s.id === service.idsurvei)
    }

    const toggleService = service => {
      const idx = selectedServices.value.findIndex(
        s => s.id === service.idsurvei
      )
      if (idx > -1) {
        selectedServices.value.splice(idx, 1)
      } else {
        selectedServices.value.push({
          id: service.idsurvei,
          text: service.keterangan
        })
      }
    }

    const onSelectRating = async star => {
      const changed = rating.value !== star
      rating.value = star
      if (changed) {
        selectedServices.value = []
        await getKomentarList(star)
      }
    }

    const isFormComplete = computed(() => {
      return (
        rating.value > 0 &&
        selectedServices.value.length >= 3 &&
        rekomendasi.value !== null
      )
    })

    const submitSurvei = async () => {
      try {
        const payload = {
          uuid: route.query.uuid,
          survei: rating.value,
          komentar: komentar.value,
          tags: selectedServices.value,
          rekomendasi: rekomendasi.value
        }

        const result = await submitSurveiRating(payload)

        if (result.status) {
          showNotif({
            status: 'success',
            message: result.message || 'Terima kasih atas penilaian Anda!'
          })
          router.push({ name: 'Hasil' })
        } else {
          showNotif({
            status: 'error',
            message: result.message || 'Gagal mengirim survei'
          })
        }
      } catch (error) {
        // error already handled in submitSurveiRating
      }
    }

    onMounted(() => {
      nextTick(() => {
        title.value = 'Survei Kepuasan Pelanggan'
      })
    })

    return {
      rating,
      hoveredStar,
      komentar,
      rekomendasi,
      selectedServices,
      komentarList,
      komentarTitle,
      loading,
      isServiceSelected,
      isFormComplete,
      onSelectRating,
      toggleService,
      submitSurvei
    }
  }
}
</script>

<style lang="scss" scoped>
.star-icon {
  font-size: 40px;
  cursor: pointer;
  transition: color 0.15s ease, transform 0.15s ease;

  &:hover {
    transform: scale(1.15);
  }
}

.star-active {
  color: #f59e0b;
}

.star-inactive {
  color: #d1d5db;
}

.service-card {
  cursor: pointer;
  transition: all 0.15s ease;
  border: 2px solid transparent;

  &-inactive {
    background: #f8f9fa;
    color: #6c757d;
    border-color: #e2e8f0;
  }

  &-active {
    background: var(--bs-primary);
    color: #fff;
    border-color: var(--bs-primary);
  }
}

.recommendation-card {
  border: 1px solid #e2e8f0;
}
</style>
